import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ExternalLink, Pencil, Plus, RefreshCw, Undo2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';

import { StatusBadge, StatusTone } from '../../components/design-system/admin/StatusBadge';
import { RelativeTime } from '../../components/design-system/admin/RelativeTime';
import {
    EscalationContact,
    STORE_FACT_TOPICS,
    StoreFactView,
    StoreKnowledgeApi,
    StoreKnowledgeStatus,
    StoreKnowledgeView,
} from '../../common/hooks/use-store-knowledge-api.hook';

/** How often the card re-reads while a crawl is running. */
export const POLL_MS = 4000;

const TONE: Record<StoreKnowledgeStatus, StatusTone> = {
    never: 'neutral',
    crawling: 'info',
    ready: 'success',
    empty: 'neutral',
    failed: 'danger',
    blocked: 'warning',
    no_site: 'neutral',
};

const hostOf = (url: string) => {
    try {
        return new URL(url).host.replace(/^www\./, '');
    } catch {
        return url;
    }
};

/**
 * What the shopping assistant knows about the shop, read from the vendor's own
 * website: status, the facts by subject with the page each one is on, a
 * "Read again now" button — and, where `api` allows edits, corrections, the
 * vendor's own facts and who shoppers are sent to when the facts run out.
 *
 * Content only, no frame: the chat settings put it in a settings group, the
 * super-admin vendor page in a section card.
 */
export function StoreKnowledgePanel({ api }: { api: StoreKnowledgeApi }) {
    const { t } = useTranslation();
    // API hooks return new objects every render; effects must not follow them.
    const apiRef = useRef(api);
    apiRef.current = api;

    const [view, setView] = useState<StoreKnowledgeView | null>(null);
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setView(await apiRef.current.load());
            setFailed(false);
        } catch {
            setFailed(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // A crawl takes a minute or two: follow it until it lands.
    const crawling = view?.status === 'crawling';
    useEffect(() => {
        if (!crawling) return undefined;
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [crawling, load]);

    const recrawl = async () => {
        setBusy(true);
        setNotice(null);
        try {
            setView(await apiRef.current.recrawl());
        } catch (err: any) {
            setNotice(err?.response?.status === 429 || err?.status === 429 ? t('SHOPPER_UI.STORE.RECRAWL_WAIT') : t('SHOPPER_UI.STORE.RECRAWL_FAILED'));
        } finally {
            setBusy(false);
        }
    };

    const edit = apiRef.current.editFact
        ? async (change: Parameters<NonNullable<StoreKnowledgeApi['editFact']>>[0]) => {
            try {
                setView(await apiRef.current.editFact!(change));
                return true;
            } catch {
                setNotice(t('SHOPPER_UI.STORE.SAVE_FAILED'));
                return false;
            }
        }
        : undefined;

    if (failed && !view) {
        return <p className="text-xs text-muted-foreground" data-testid="store-knowledge-error">{t('SHOPPER_UI.STORE.LOAD_FAILED')}</p>;
    }
    if (!view) {
        return (
            <div className="flex flex-col gap-2" data-testid="store-knowledge-loading">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-16 w-full" />
            </div>
        );
    }

    const visible = view.facts.filter((f) => !f.hidden);
    const hidden = view.facts.filter((f) => f.hidden);
    const canRecrawl = view.enabled && view.status !== 'no_site' && !crawling;

    return (
        <div className="flex flex-col gap-4" data-testid="store-knowledge">
            {/* Status */}
            <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <StatusBadge tone={TONE[view.status] ?? 'neutral'}>
                        <span data-testid="store-knowledge-status">{t(`SHOPPER_UI.STORE.STATUS.${view.status}`)}</span>
                    </StatusBadge>
                    {canRecrawl && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={recrawl}
                            disabled={busy}
                            data-testid="store-knowledge-recrawl"
                        >
                            <RefreshCw className={busy ? 'animate-spin' : undefined} />
                            {t('SHOPPER_UI.STORE.RECRAWL')}
                        </Button>
                    )}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {view.website && (
                        <a href={view.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground">
                            {hostOf(view.website)} <ExternalLink className="size-3" aria-hidden />
                        </a>
                    )}
                    {view.crawledAt && (
                        <span>
                            {t('SHOPPER_UI.STORE.LAST_READ')} <RelativeTime value={view.crawledAt} />
                        </span>
                    )}
                </div>
                {!view.enabled && <p className="text-xs text-muted-foreground">{t('SHOPPER_UI.STORE.DISABLED')}</p>}
                {view.status === 'no_site' && <p className="text-xs text-muted-foreground">{t('SHOPPER_UI.STORE.NO_SITE_HINT')}</p>}
                {view.status === 'blocked' && (
                    <p className="text-xs text-muted-foreground" data-testid="store-knowledge-blocked">{t('SHOPPER_UI.STORE.BLOCKED_HINT')}</p>
                )}
                {view.error && view.status !== 'no_site' && view.status !== 'blocked' && <p className="text-xs text-muted-foreground">{view.error}</p>}
                {notice && <p className="text-xs text-muted-foreground" role="status" data-testid="store-knowledge-notice">{notice}</p>}
            </div>

            {/* Facts by subject */}
            {visible.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('SHOPPER_UI.STORE.NOTHING_YET')}</p>
            ) : (
                <div className="flex flex-col gap-3">
                    {STORE_FACT_TOPICS.map((topic) => {
                        const facts = visible.filter((f) => f.topic === topic);
                        if (!facts.length) return null;
                        return (
                            <div key={topic} className="flex flex-col gap-1.5" data-testid={`store-topic-${topic}`}>
                                <p className="text-xs font-medium text-muted-foreground">{t(`SHOPPER_UI.STORE.TOPICS.${topic}`)}</p>
                                <ul className="flex flex-col divide-y rounded-lg border">
                                    {facts.map((fact) => <FactRow key={fact.id} fact={fact} onEdit={edit} />)}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            )}

            {hidden.length > 0 && edit && (
                <Collapsible>
                    <CollapsibleTrigger className="group/removed flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                        {t('SHOPPER_UI.STORE.REMOVED', { count: hidden.length })}
                        <ChevronDown className="size-3 transition-transform group-data-[state=open]/removed:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <ul className="mt-2 flex flex-col gap-1.5">
                            {hidden.map((fact) => (
                                <li key={fact.id} className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                                    <span className="min-w-0 truncate line-through">{fact.value}</span>
                                    <Button variant="ghost" size="xs" onClick={() => edit({ id: fact.id, value: fact.value })}>
                                        <Undo2 />{t('SHOPPER_UI.STORE.RESTORE')}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    </CollapsibleContent>
                </Collapsible>
            )}

            {edit && <AddFact onAdd={edit} />}
            {apiRef.current.setContact && (
                <ContactForm
                    contact={view.contact}
                    onSave={async (contact) => {
                        try {
                            setView(await apiRef.current.setContact!(contact));
                            return true;
                        } catch {
                            setNotice(t('SHOPPER_UI.STORE.SAVE_FAILED'));
                            return false;
                        }
                    }}
                />
            )}

            {/* The pages read */}
            {view.pages.length > 0 && (
                <Collapsible>
                    <CollapsibleTrigger className="group/pages flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="store-knowledge-pages-toggle">
                        {t('SHOPPER_UI.STORE.PAGES', { count: view.pages.length })}
                        <ChevronDown className="size-3 transition-transform group-data-[state=open]/pages:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <ul className="mt-2 flex flex-col gap-1">
                            {view.pages.map((page) => (
                                <li key={page.url} className="flex items-center justify-between gap-2 text-xs">
                                    <a href={page.url} target="_blank" rel="noreferrer" className="min-w-0 truncate text-muted-foreground hover:text-foreground">
                                        {page.url.replace(/^https?:\/\/(www\.)?/, '')}
                                    </a>
                                    <span className="shrink-0 text-muted-foreground">
                                        {page.error ? t('SHOPPER_UI.STORE.PAGE_FAILED') : page.facts}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
}

/** One fact: what it is, what it says, where it came from — and, for the vendor, edit or remove. */
function FactRow({ fact, onEdit }: { fact: StoreFactView; onEdit?: (change: any) => Promise<boolean> }) {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(fact.value);
    const field = t(`SHOPPER_UI.STORE.FIELDS.${fact.field.toUpperCase()}`, { defaultValue: fact.field });

    const save = async (e: FormEvent) => {
        e.preventDefault();
        if (!value.trim() || !onEdit) return;
        if (await onEdit({ id: fact.id, value: value.trim() })) setEditing(false);
    };

    if (editing) {
        return (
            <li className="px-3 py-2">
                <form onSubmit={save} className="flex items-center gap-1.5">
                    <Input value={value} onChange={(e) => setValue(e.target.value)} className="h-7 text-sm" aria-label={field} autoFocus />
                    <Button type="submit" variant="outline" size="sm">{t('SHOPPER_UI.STORE.SAVE')}</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setValue(fact.value); setEditing(false); }}>
                        {t('SHOPPER_UI.STORE.CANCEL')}
                    </Button>
                </form>
            </li>
        );
    }

    return (
        <li className="group/fact flex items-start gap-2 px-3 py-2" data-testid="store-fact">
            <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">
                    {fact.label ? `${fact.label} · ` : ''}{field}
                </p>
                <p className="text-sm break-words">{fact.value}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                    {fact.sourceUrl && (
                        <a href={fact.sourceUrl} target="_blank" rel="noreferrer" title={fact.quote ?? undefined} className="inline-flex items-center gap-1 hover:text-foreground">
                            {t('SHOPPER_UI.STORE.SOURCE')} <ExternalLink className="size-3" aria-hidden />
                        </a>
                    )}
                    {fact.origin === 'vendor' ? <span>{t('SHOPPER_UI.STORE.FROM_YOU')}</span> : fact.locked ? <span>{t('SHOPPER_UI.STORE.EDITED')}</span> : null}
                </div>
            </div>
            {onEdit && (
                <div className="flex shrink-0 gap-0.5 opacity-60 group-hover/fact:opacity-100">
                    <Button variant="ghost" size="icon-xs" aria-label={t('SHOPPER_UI.STORE.EDIT') as string} onClick={() => setEditing(true)}>
                        <Pencil />
                    </Button>
                    <Button variant="ghost" size="icon-xs" aria-label={t('SHOPPER_UI.STORE.REMOVE') as string} onClick={() => onEdit({ id: fact.id, remove: true })} data-testid="store-fact-remove">
                        <X />
                    </Button>
                </div>
            )}
        </li>
    );
}

/** The vendor's own fact, for what the website does not say. */
function AddFact({ onAdd }: { onAdd: (change: any) => Promise<boolean> }) {
    const { t } = useTranslation();
    const [topic, setTopic] = useState<string>('showrooms');
    const [value, setValue] = useState('');

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        if (!value.trim()) return;
        const field = topic === 'other' ? 'fact' : topic === 'supplies' ? 'product' : 'details';
        if (await onAdd({ topic, field, value: value.trim() })) setValue('');
    };

    return (
        <form onSubmit={submit} className="flex flex-col gap-1.5" data-testid="store-add-fact">
            <p className="text-xs font-medium text-muted-foreground">{t('SHOPPER_UI.STORE.ADD_TITLE')}</p>
            <div className="flex flex-wrap items-center gap-1.5">
                <NativeSelect size="sm" value={topic} onChange={(e) => setTopic(e.target.value)} aria-label={t('SHOPPER_UI.STORE.ADD_TITLE') as string}>
                    {STORE_FACT_TOPICS.map((tp) => (
                        <NativeSelectOption key={tp} value={tp}>{t(`SHOPPER_UI.STORE.TOPICS.${tp}`)}</NativeSelectOption>
                    ))}
                </NativeSelect>
                <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={t('SHOPPER_UI.STORE.ADD_PLACEHOLDER') as string}
                    className="h-7 min-w-0 flex-1 text-sm"
                    data-testid="store-add-fact-input"
                />
                <Button type="submit" variant="outline" size="sm" disabled={!value.trim()}>
                    <Plus />{t('SHOPPER_UI.STORE.ADD')}
                </Button>
            </div>
            <p className="text-xs text-muted-foreground">{t('SHOPPER_UI.STORE.LOCK_HINT')}</p>
        </form>
    );
}

/** Who shoppers are sent to when the facts do not answer. */
function ContactForm({ contact, onSave }: { contact: EscalationContact | null; onSave: (c: EscalationContact) => Promise<boolean> }) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<EscalationContact>(contact ?? {});
    const [saved, setSaved] = useState(false);
    useEffect(() => setDraft(contact ?? {}), [contact]);

    const field = (key: keyof EscalationContact, label: string, type = 'text', placeholder?: string) => (
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {label}
            <Input
                type={type}
                value={draft[key] ?? ''}
                placeholder={placeholder}
                onChange={(e) => { setSaved(false); setDraft({ ...draft, [key]: e.target.value }); }}
                className="h-8 text-sm"
                data-testid={`store-contact-${key}`}
            />
        </label>
    );

    return (
        <form
            className="flex flex-col gap-2 rounded-lg border p-3"
            onSubmit={async (e) => { e.preventDefault(); setSaved(await onSave(draft)); }}
            data-testid="store-contact"
        >
            <div>
                <p className="text-sm font-medium">{t('SHOPPER_UI.STORE.CONTACT_TITLE')}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t('SHOPPER_UI.STORE.CONTACT_DESC')}</p>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {field('phone', t('SHOPPER_UI.STORE.PHONE'), 'tel')}
                {field('email', t('SHOPPER_UI.STORE.EMAIL'), 'email')}
                {field('bookingUrl', t('SHOPPER_UI.STORE.BOOKING'), 'url', 'https://')}
                {field('ordersUrl', t('SHOPPER_UI.STORE.ORDERS_URL'), 'url', 'https://')}
                {field('note', t('SHOPPER_UI.STORE.NOTE'), 'text', t('SHOPPER_UI.STORE.NOTE_PLACEHOLDER') as string)}
            </div>
            <div className="flex items-center gap-2">
                <Button type="submit" variant="outline" size="sm" data-testid="store-contact-save">{t('SHOPPER_UI.STORE.SAVE')}</Button>
                {saved && <span className="text-xs text-muted-foreground">{t('SHOPPER_UI.STORE.SAVED')}</span>}
            </div>
        </form>
    );
}
